import { useMemo, useState } from "react";
import { Filter } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox.jsx";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.jsx";
import { cn } from "@/lib/utils.js";

/**
 * Excel-style multi-select vendor filter for classification grid headers.
 * Reusable across Needs Mapping and Your Mappings.
 *
 * Selection semantics:
 * - `null` — no filter (all vendors shown / checked)
 * - `[]` — cleared (no rows shown)
 * - `string[]` — only those vendors shown
 *
 * @param {Object} props
 * @param {string[]} props.vendors - Unique vendor options (typically assembled from all rows loaded into the grid)
 * @param {string[]|null} props.selectedVendors - Selected vendors (`null` = all)
 * @param {(vendors: string[]|null) => void} props.onChange - Called when selection changes
 * @returns {JSX.Element}
 */
export default function VendorColumnFilter({
  vendors,
  selectedVendors,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const isFiltered = selectedVendors !== null;
  const canClear = selectedVendors === null || selectedVendors.length > 0;

  const filteredVendors = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return vendors;
    return vendors.filter((vendor) => vendor.toLowerCase().includes(term));
  }, [vendors, searchTerm]);

  /**
   * Toggles a vendor in the filter selection.
   * @param {string} vendor
   */
  const toggleVendor = (vendor) => {
    const current = selectedVendors === null ? vendors : selectedVendors;
    const next = current.includes(vendor)
      ? current.filter((v) => v !== vendor)
      : [...current, vendor];

    // Re-selecting every vendor returns to the unfiltered state
    onChange(next.length === vendors.length ? null : next);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearchTerm("");
      }}>
      <PopoverTrigger asChild>
        <button
          type='button'
          title={isFiltered ? "Vendor filter active" : "Filter by vendor"}
          aria-label='Filter by vendor'
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex items-center justify-center rounded p-0.5 transition-colors",
            isFiltered
              ? "text-primary hover:bg-primary/10"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          )}>
          <Filter
            className='h-3.5 w-3.5'
            fill={isFiltered ? "currentColor" : "none"}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-56 p-0'
        onClick={(e) => e.stopPropagation()}>
        <div className='p-2 border-b border-border space-y-2'>
          <button
            type='button'
            onClick={() => onChange(null)}
            disabled={selectedVendors === null}
            className='text-xs text-primary hover:text-primary/90 px-0.5 py-0.5 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed'>
            Select all
          </button>
          <input
            type='text'
            placeholder='Search vendors...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='w-full px-2 py-1 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:border-primary'
          />
        </div>
        <div className='max-h-60 overflow-y-auto p-1'>
          {filteredVendors.length === 0 ? (
            <div className='px-2 py-3 text-sm text-muted-foreground text-center'>
              No vendors found
            </div>
          ) : (
            filteredVendors.map((vendor) => {
              const checked =
                selectedVendors === null || selectedVendors.includes(vendor);
              return (
                <label
                  key={vendor}
                  className='flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer select-none'>
                  <Checkbox
                    checked={checked}
                    onChange={() => toggleVendor(vendor)}
                    className='shrink-0'
                  />
                  <span className='text-sm text-foreground truncate'>
                    {vendor}
                  </span>
                </label>
              );
            })
          )}
        </div>
        <div className='p-2 border-t border-border'>
          <button
            type='button'
            onClick={() => onChange([])}
            disabled={!canClear}
            className='w-full text-left px-2 py-1.5 text-xs text-primary hover:text-primary/90 hover:bg-muted rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent'>
            Select None
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
